import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import { Plus, Upload, FileText, X, ChevronRight, Sparkles, Check, Trash2, Edit2, AlertCircle, BookOpen, Bot, Search, MessageSquare, Link2, CheckCircle, XCircle, Download, FileDown, Wand2 } from 'lucide-react';
import RowActions from '../components/RowActions';
import { useConfirm } from '../components/ConfirmDialog';
import ImportAIModal from '../components/ImportAIModal';

const statoLabels = { bozza: 'Bozza', importato: 'Importato', in_revisione: 'In Revisione', validato: 'Validato', preventivo_generato: 'Preventivo Generato' };
const statoColors = { bozza: 'bg-slate-100 text-slate-700', importato: 'bg-blue-100 text-blue-700', in_revisione: 'bg-yellow-100 text-yellow-800', validato: 'bg-green-100 text-green-700', preventivo_generato: 'bg-purple-100 text-purple-700' };
const rigaStatoColors = { da_validare: 'bg-yellow-100 text-yellow-800', validata: 'bg-green-100 text-green-700', review_required: 'bg-red-100 text-red-700' };

export default function ComputiPage({ onNavigate, context }) {
  const confirm = useConfirm();
  const [computi, setComputi] = useState([]);
  const [selected, setSelected] = useState(null);
  const [righe, setRighe] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rowForm, setRowForm] = useState({ capitolo: '', capitolo_titolo: '', codice_attivita: '', descrizione: '', unita_misura: '', quantita: '', prezzo_riferimento: '', prezzo_offerto: '', formula_calcolo: '', esclusioni: '', inclusioni: '', note_tecniche: '', flag_a_corpo: false, flag_stimata: false });
  const [prezzSearch, setPrezzSearch] = useState('');
  const [prezzResults, setPrezzResults] = useState([]);
  const [cataloghi, setCataloghi] = useState([]);
  const [selectedCatalogo, setSelectedCatalogo] = useState('');
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showRevisione, setShowRevisione] = useState(false);
  const [revisione, setRevisione] = useState('');
  const [revisioneLoading, setRevisioneLoading] = useState(false);
  const [showImportAI, setShowImportAI] = useState(false);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [suggDesc, setSuggDesc] = useState('');
  const [showMatching, setShowMatching] = useState(false);
  const [matchResults, setMatchResults] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSelected, setMatchSelected] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(() => API.get('/api/computi').then(r => setComputi(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/api/prezzario/cataloghi').then(r => {
      setCataloghi(r.data);
      // Default: preferisci CER 2025 (catalogo aggiornato), altrimenti il primo disponibile
      if (r.data.length && !selectedCatalogo) {
        const cer = r.data.find(c => c.id === 'cer_2025');
        setSelectedCatalogo(cer ? 'cer_2025' : r.data[0].id);
      }
    });
  }, []);

  const loadRighe = useCallback((cid) => API.get(`/api/computi/${cid}/righe`).then(r => setRighe(r.data)), []);

  const selectComputo = useCallback(async (c) => {
    setSelected(c);
    loadRighe(c.id);
  }, [loadRighe]);

  // Apertura automatica del computo richiesto da PraticaHub
  useEffect(() => {
    if (!context?.computo_id || !computi || computi.length === 0 || selected) return;
    const target = computi.find(c => c.id === context.computo_id);
    if (target) selectComputo(target);
  }, [context?.computo_id, computi, selected, selectComputo]);

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('pratica_id', '');
    try {
      const { data } = await API.post('/api/computi/upload', formData);
      load();
      if (data.computo?.id) {
        const computo = await API.get(`/api/computi/${data.computo.id}`);
        selectComputo(computo.data);
      }
    } catch (err) {
      alert('Errore nel caricamento: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const createManual = async () => {
    const { data } = await API.post('/api/computi', { titolo: 'Nuovo Computo Metrico', origine: 'interno' });
    load();
    selectComputo(data);
  };

  // Import AI: crea un nuovo computo e ne carica le righe estratte dall'AI.
  const handleImportAI = async (voci, meta, sampleId) => {
    const titoloDefault = meta?.filename
      ? `Importato: ${meta.filename.replace(/\.[^.]+$/, '')}`
      : 'Importato con AI';
    const { data: nuovo } = await API.post('/api/computi', {
      titolo: titoloDefault,
      origine: 'importato',
      pratica_id: context?.pratica_id || undefined,
    });
    // Inserisci righe in sequenza (mantiene ordine). SpCat/categoria -> capitolo (A, B, C...)
    const catToLetter = {};
    let nextLetter = 65;
    for (let i = 0; i < voci.length; i++) {
      const v = voci[i];
      const isCorpo = v.tipo === 'corpo';
      const qta = isCorpo ? 1 : Number(v.quantita || 0);
      const pu = isCorpo
        ? Number(v.importo || v.prezzo_unitario || 0)
        : Number(v.prezzo_unitario || 0);
      const cat = (v.categoria || '').trim();
      let capLetter = '';
      if (cat) {
        if (!catToLetter[cat]) { catToLetter[cat] = String.fromCharCode(nextLetter); nextLetter += 1; }
        capLetter = catToLetter[cat];
      }
      await API.post(`/api/computi/${nuovo.id}/righe`, {
        capitolo: capLetter,
        capitolo_titolo: cat,
        codice_attivita: v.codice || '',
        descrizione: v.descrizione,
        formula_calcolo: v.formula || '',
        unita_misura: isCorpo ? 'a corpo' : (v.unita_misura || ''),
        quantita: qta,
        prezzo_offerto: pu,
        prezzo_riferimento: pu,
        flag_a_corpo: isCorpo,
        ordine: i + 1,
      });
    }
    // Registra le voci confermate come "ground truth" per il dataset training
    if (sampleId) {
      API.post(`/api/import/${sampleId}/confirm`, { voci }).catch(() => {});
    }
    setShowImportAI(false);
    await load();
    await selectComputo(nuovo);
  };

  const [aiProgress, setAiProgress] = useState('');
  const [aiJobId, setAiJobId] = useState(null);

  const generateAIComputo = async () => {
    if (!aiDesc.trim()) return;
    setAiLoading(true);
    setAiProgress('Avvio generazione...');
    try {
      const { pollAiJob } = await import('../aiJobs');
      const { result, error, jobId } = await pollAiJob(
        '/api/ai/genera-computo',
        { descrizione: aiDesc, catalogo: selectedCatalogo || '' },
        { onProgress: setAiProgress }
      );
      setAiJobId(jobId);
      if (error) {
        alert('Errore generazione AI: ' + error);
      } else if (result?.computo?.id) {
        setShowAIGenerate(false);
        setAiDesc('');
        await load();
        const computo = await API.get(`/api/computi/${result.computo.id}`);
        selectComputo(computo.data);
      }
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setAiLoading(false);
    setAiProgress('');
    setAiJobId(null);
  };

  const revisionaComputo = async () => {
    if (!selected) return;
    setRevisioneLoading(true);
    setShowRevisione(true);
    setRevisione('');
    try {
      const { pollAiJob } = await import('../aiJobs');
      const { result, error } = await pollAiJob(
        `/api/ai/revisiona-computo/${selected.id}`,
        {},
        { onProgress: (p) => setRevisione('  ' + p) }
      );
      if (error) {
        setRevisione('Errore: ' + error);
      } else {
        setRevisione(result?.revisione || 'Nessun suggerimento');
      }
    } catch (err) {
      setRevisione('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setRevisioneLoading(false);
  };

  const suggerisciVoci = async () => {
    if (!suggDesc.trim()) return;
    setSuggLoading(true);
    try {
      const { data } = await API.post('/api/ai/suggerisci-voci', { descrizione: suggDesc, catalogo: selectedCatalogo || '' });
      setSuggerimenti(data.suggerimenti || []);
    } catch {
      setSuggerimenti([]);
    }
    setSuggLoading(false);
  };

  const addFromSuggerimento = (s) => {
    setRowForm(f => ({
      ...f,
      codice_attivita: s.codice || '',
      descrizione: s.descrizione || '',
      unita_misura: s.unita_misura || '',
      prezzo_riferimento: String(s.prezzo || ''),
      prezzo_offerto: String(s.prezzo || ''),
    }));
    setShowSuggerimenti(false);
    setShowNewRow(true);
  };

  const runMatching = async () => {
    if (!selected) return;
    setMatchLoading(true);
    setShowMatching(true);
    setMatchResults([]);
    setMatchSelected({});
    try {
      const { pollAiJob } = await import('../aiJobs');
      const { result, error } = await pollAiJob(
        `/api/ai/match-computo/${selected.id}`,
        {},
        { onProgress: (p) => console.log('Match progress:', p) }
      );
      if (error) {
        alert('Errore matching: ' + error);
        setShowMatching(false);
      } else {
        const matches = result?.matches || [];
        setMatchResults(matches);
        // Auto-select all alta/media confidence matches
        const sel = {};
        matches.forEach(m => {
          if (m.codice_match && m.codice_match !== 'nessun_match' && (m.confidenza === 'alta' || m.confidenza === 'media')) {
            sel[m.riga_id] = true;
          }
        });
        setMatchSelected(sel);
      }
    } catch (err) {
      alert('Errore matching: ' + (err.response?.data?.detail || err.message));
      setShowMatching(false);
    }
    setMatchLoading(false);
  };

  const toggleMatch = (rigaId) => {
    setMatchSelected(s => ({ ...s, [rigaId]: !s[rigaId] }));
  };

  const applySelectedMatches = async () => {
    const toApply = matchResults.filter(m => matchSelected[m.riga_id] && m.codice_match && m.codice_match !== 'nessun_match');
    if (!toApply.length) return;
    try {
      const { data } = await API.post('/api/ai/applica-match-batch', { matches: toApply });
      alert(`${data.applicati} match applicati su ${data.totale}`);
      setShowMatching(false);
      loadRighe(selected.id);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const applySingleMatch = async (match) => {
    try {
      await API.post('/api/ai/applica-match', match);
      setMatchResults(prev => prev.filter(m => m.riga_id !== match.riga_id));
      loadRighe(selected.id);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const searchPrezzario = async (q) => {
    setPrezzSearch(q);
    if (q.length >= 3) {
      // Semantic search prioritaria
      const semParams = new URLSearchParams({ q, top_k: '15', solo_opere: 'true' });
      if (selectedCatalogo) semParams.set('catalogo', selectedCatalogo);
      try {
        const r = await API.get(`/api/prezzario/semantic-search?${semParams.toString()}`);
        setPrezzResults(r.data?.results || []);
        return;
      } catch (e) {}
      // Fallback su keyword search
      const params = { q, limit: 10 };
      if (selectedCatalogo) params.catalogo = selectedCatalogo;
      const { data } = await API.get('/api/prezzario', { params });
      setPrezzResults(data.items || []);
    } else if (q.length >= 2) {
      const params = { q, limit: 10 };
      if (selectedCatalogo) params.catalogo = selectedCatalogo;
      const { data } = await API.get('/api/prezzario', { params });
      setPrezzResults(data.items || []);
    } else {
      setPrezzResults([]);
    }
  };

  const addFromPrezzario = (voce) => {
    setRowForm(f => ({
      ...f,
      codice_attivita: voce.codice,
      descrizione: voce.descrizione,
      unita_misura: voce.unita_misura,
      prezzo_riferimento: String(voce.prezzo),
      prezzo_offerto: String(voce.prezzo),
    }));
    setPrezzSearch('');
    setPrezzResults([]);
  };

  const saveRow = async () => {
    const payload = {
      ...rowForm,
      quantita: parseFloat(rowForm.quantita) || 0,
      prezzo_riferimento: parseFloat(rowForm.prezzo_riferimento) || 0,
      prezzo_offerto: parseFloat(rowForm.prezzo_offerto) || 0,
    };
    if (editingRow) {
      await API.put(`/api/computi/${selected.id}/righe/${editingRow}`, payload);
    } else {
      await API.post(`/api/computi/${selected.id}/righe`, payload);
    }
    setShowNewRow(false);
    setEditingRow(null);
    setRowForm({ capitolo: '', capitolo_titolo: '', codice_attivita: '', descrizione: '', unita_misura: '', quantita: '', prezzo_riferimento: '', prezzo_offerto: '', formula_calcolo: '', esclusioni: '', inclusioni: '', note_tecniche: '', flag_a_corpo: false, flag_stimata: false });
    loadRighe(selected.id);
  };

  const editRow = (r) => {
    setRowForm({
      capitolo: r.capitolo || '', capitolo_titolo: r.capitolo_titolo || '', codice_attivita: r.codice_attivita || r.codice || '',
      descrizione: r.descrizione || '', unita_misura: r.unita_misura || '',
      quantita: String(r.quantita || ''), prezzo_riferimento: String(r.prezzo_riferimento || ''),
      prezzo_offerto: String(r.prezzo_offerto || ''),
      formula_calcolo: r.formula_calcolo || '', esclusioni: r.esclusioni || '',
      inclusioni: r.inclusioni || '', note_tecniche: r.note_tecniche || '',
      flag_a_corpo: !!r.flag_a_corpo, flag_stimata: !!r.flag_stimata,
    });
    setEditingRow(r.id);
    setShowNewRow(true);
  };

  const deleteRow = async (rid) => {
    if (await confirm({ title: 'Eliminare riga?', message: 'La riga verrà rimossa dal computo.', confirmLabel: 'Elimina', variant: 'danger' })) {
      await API.delete(`/api/computi/${selected.id}/righe/${rid}`);
      loadRighe(selected.id);
    }
  };

  const downloadFile = async (path, filename, mime) => {
    try {
      const r = await API.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Errore download: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadPdf = () => downloadFile(`/api/computi/${selected.id}/pdf`, `Computo_${selected.codice_computo}.pdf`, 'application/pdf');
  const downloadXlsx = () => downloadFile(`/api/computi/${selected.id}/excel`, `Computo_${selected.codice_computo}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  const validateRow = async (rid) => {
    await API.put(`/api/computi/${selected.id}/righe/${rid}`, { stato_riga: 'validata' });
    loadRighe(selected.id);
  };

  const generaPreventivo = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post(`/api/computi/${selected.id}/genera-preventivo`);
      alert(`Preventivo ${data.numero_preventivo} generato con successo!`);
      const updated = await API.get(`/api/computi/${selected.id}`);
      setSelected(updated.data);
      load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const totale = righe.reduce((s, r) => s + (r.totale_riga || 0), 0);

  // Detail view
  if (selected) {
    return (
      <div className="animate-fade-in" data-testid="computo-detail">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setSelected(null); setRighe([]); }} className="p-2 hover:bg-slate-100 rounded-md" data-testid="back-computi">
            <ChevronRight size={20} className="rotate-180" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold tracking-tight">{selected.titolo || selected.codice_computo}</h1>
            <p className="text-sm text-slate-500">{selected.codice_computo} - v{selected.versione} - {selected.origine}</p>
          </div>
          <span className={`px-3 py-1 rounded text-xs font-bold ${statoColors[selected.stato] || 'bg-slate-100'}`}>
            {statoLabels[selected.stato] || selected.stato}
          </span>
          {righe.length > 0 && selected.stato !== 'preventivo_generato' && (
            <button onClick={generaPreventivo} disabled={generating} data-testid="genera-preventivo-btn"
              className="px-4 py-2 bg-accent-success text-white font-bold rounded-md hover:bg-green-700 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50">
              <Sparkles size={16} /> {generating ? 'Generazione...' : 'Genera Preventivo'}
            </button>
          )}
        </div>

        {/* Totali */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-xs font-bold text-slate-500 uppercase">Righe</p>
            <p className="text-xl font-black">{righe.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-xs font-bold text-slate-500 uppercase">Da Validare</p>
            <p className="text-xl font-black text-yellow-600">{righe.filter(r => r.stato_riga !== 'validata').length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-xs font-bold text-slate-500 uppercase">Totale</p>
            <p className="text-xl font-black">{fmt(totale)}</p>
          </div>
        </div>

        {/* Add row toolbar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={() => { setShowNewRow(true); setEditingRow(null); setRowForm({ capitolo: '', capitolo_titolo: '', codice_attivita: '', descrizione: '', unita_misura: '', quantita: '', prezzo_riferimento: '', prezzo_offerto: '', formula_calcolo: '', esclusioni: '', inclusioni: '', note_tecniche: '', flag_a_corpo: false, flag_stimata: false }); }}
            className="px-3 py-1.5 bg-primary text-white text-sm font-bold rounded-md hover:bg-primary-hover transition-all flex items-center gap-1" data-testid="add-riga-btn">
            <Plus size={14} /> Aggiungi Riga
          </button>
          <button onClick={() => setShowSuggerimenti(true)} data-testid="ai-suggerisci-btn"
            className="px-3 py-1.5 bg-surface-dark text-white text-sm font-bold rounded-md hover:bg-slate-700 transition-all flex items-center gap-1">
            <Search size={14} /> AI Cerca Voce
          </button>
          <button onClick={runMatching} data-testid="ai-match-btn"
            className="px-3 py-1.5 bg-purple-600 text-white text-sm font-bold rounded-md hover:bg-purple-700 transition-all flex items-center gap-1">
            <Link2 size={14} /> Match Automatico
          </button>
          {righe.length > 0 && (
            <button onClick={revisionaComputo} data-testid="ai-revisiona-btn"
              className="px-3 py-1.5 bg-accent-info text-white text-sm font-bold rounded-md hover:bg-blue-700 transition-all flex items-center gap-1">
              <Bot size={14} /> AI Revisiona
            </button>
          )}
          {righe.length > 0 && (
            <>
              <button onClick={downloadPdf} data-testid="download-pdf-btn"
                className="ml-auto px-3 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 transition-all flex items-center gap-1">
                <FileDown size={14} /> PDF
              </button>
              <button onClick={downloadXlsx} data-testid="download-excel-btn"
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md hover:bg-emerald-700 transition-all flex items-center gap-1">
                <Download size={14} /> Excel
              </button>
            </>
          )}
        </div>

        {/* Rows table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="righe-computo-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500 w-8">#</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Capitolo</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Codice</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Descrizione</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500 w-14">U.M.</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500 w-16">Qtà</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500 w-24">Rif.</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500 w-24">Offerto</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500 w-24">Totale</th>
                  <th className="text-center px-3 py-2 text-xs uppercase font-bold text-slate-500 w-20">Stato</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500 w-24">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Raggruppa righe per capitolo (mantenendo ordine)
                  const groups = {};
                  const order = [];
                  righe.forEach(r => {
                    const key = r.capitolo || '__BASE__';
                    if (!groups[key]) {
                      groups[key] = { titolo: r.capitolo_titolo || '', righe: [] };
                      order.push(key);
                    }
                    groups[key].righe.push(r);
                    if (r.capitolo_titolo && !groups[key].titolo) groups[key].titolo = r.capitolo_titolo;
                  });
                  const out = [];
                  order.forEach(key => {
                    const g = groups[key];
                    const subTot = g.righe.reduce((s, x) => s + (x.totale_riga || 0), 0);
                    if (key !== '__BASE__') {
                      out.push(
                        <tr key={`cap-${key}`} className="bg-slate-900 text-white">
                          <td colSpan={11} className="px-3 py-1.5 text-xs font-black tracking-wider uppercase">
                            CAPITOLO {key}{g.titolo ? ` - ${g.titolo}` : ''}
                          </td>
                        </tr>
                      );
                    }
                    g.righe.forEach((r, idx) => {
                      const visibleIdx = righe.findIndex(x => x.id === r.id);
                      out.push(
                        <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${r.stato_riga === 'review_required' ? 'bg-red-50/50' : ''}`} data-testid={`riga-${r.id}`}>
                          <td className="px-3 py-2 text-xs text-slate-400">{r.ordine || (visibleIdx + 1)}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 font-bold">{r.capitolo}</td>
                          <td className="px-3 py-2 font-mono text-xs text-primary">{r.codice_attivita || r.codice}</td>
                          <td className="px-3 py-2 text-sm max-w-xs">
                            <p className="line-clamp-2">{r.descrizione}</p>
                            {r.formula_calcolo && <p className="text-[10px] italic text-blue-700 font-mono mt-0.5">= {r.formula_calcolo}</p>}
                            {r.inclusioni && <p className="text-[10px] italic text-green-700 mt-0.5"><b>compreso:</b> {r.inclusioni}</p>}
                            {r.esclusioni && <p className="text-[10px] italic text-red-700 mt-0.5"><b>escluso:</b> {r.esclusioni}</p>}
                            {r.flag_stimata && <span className="inline-block mt-0.5 px-1 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold">STIMATA</span>}
                            {r.flag_a_corpo && <span className="inline-block mt-0.5 ml-1 px-1 py-0.5 bg-indigo-100 text-indigo-800 rounded text-[9px] font-bold">A CORPO</span>}
                          </td>
                          <td className="px-3 py-2 text-xs">{r.unita_misura}</td>
                          <td className="px-3 py-2 text-right text-xs">{r.flag_a_corpo ? '1' : (r.quantita || '-')}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">{r.prezzo_riferimento ? fmt(r.prezzo_riferimento) : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs font-bold">{r.prezzo_offerto ? fmt(r.prezzo_offerto) : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs font-bold">{fmt(r.totale_riga)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${rigaStatoColors[r.stato_riga] || 'bg-slate-100'}`}>
                              {r.stato_riga === 'validata' ? 'OK' : r.stato_riga === 'review_required' ? '!' : '?'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {r.stato_riga !== 'validata' && <button onClick={() => validateRow(r.id)} className="p-1 hover:bg-green-50 rounded" title="Valida"><Check size={12} className="text-green-600" /></button>}
                            <button onClick={() => editRow(r)} className="p-1 hover:bg-slate-100 rounded" title="Modifica"><Edit2 size={12} className="text-slate-400" /></button>
                            <button onClick={() => deleteRow(r.id)} className="p-1 hover:bg-red-50 rounded" title="Elimina"><Trash2 size={12} className="text-red-400" /></button>
                          </td>
                        </tr>
                      );
                    });
                    if (key !== '__BASE__') {
                      out.push(
                        <tr key={`subtot-${key}`} className="bg-primary">
                          <td colSpan={8} className="px-3 py-1.5 text-right text-xs font-bold text-slate-700">Sommano CAPITOLO {key}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs font-black text-primary">{fmt(subTot)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      );
                    }
                  });
                  return out;
                })()}
                {righe.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Nessuna riga. Aggiungi righe manualmente o carica un file.</td></tr>}
              </tbody>
              {righe.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={8} className="px-3 py-2 text-right font-bold text-sm">TOTALE COMPUTO</td>
                    <td className="px-3 py-2 text-right font-mono font-black text-sm">{fmt(totale)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Row form modal */}
        {showNewRow && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
                <h3 className="text-lg font-bold">{editingRow ? 'Modifica Riga' : 'Nuova Riga Computo'}</h3>
                <button onClick={() => { setShowNewRow(false); setEditingRow(null); }} aria-label="Chiudi"><X size={20} className="text-slate-400" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 sm:p-6">

              {/* Prezzario search */}
              <div className="mb-4 p-3 bg-slate-50 rounded-md">
                <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><BookOpen size={12} /> Cerca nel Prezzario</p>
                {cataloghi.length > 1 && (
                  <div className="flex flex-wrap gap-1 mb-2" data-testid="computo-catalogo-switcher">
                    {cataloghi.map(c => (
                      <button key={c.id} type="button" onClick={() => { setSelectedCatalogo(c.id); if (prezzSearch.length >= 2) searchPrezzario(prezzSearch); }}
                        className={`text-[10px] px-2 py-1 rounded font-bold transition-all ${selectedCatalogo === c.id
                          ? (c.id === 'cer_2025' ? 'bg-indigo-600 text-white' : c.id === 'aziendale' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white')
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        {c.id === 'cer_2025' ? 'CER 2025' : c.id === 'lombardia_2025' ? 'Lombardia 2025' : c.nome.substring(0, 12)}
                        <span className="opacity-70 ml-1">({c.count > 999 ? `${(c.count / 1000).toFixed(1)}k` : c.count})</span>
                      </button>
                    ))}
                  </div>
                )}
                <input placeholder="Cerca voce prezzario..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" value={prezzSearch} onChange={e => searchPrezzario(e.target.value)} />
                {prezzResults.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {prezzResults.map(v => (
                      <button key={v.id} onClick={() => addFromPrezzario(v)} className="w-full text-left p-2 bg-white rounded border border-slate-200 hover:bg-primary text-xs">
                        <span className="font-mono text-primary">{v.codice}</span> - {v.descrizione?.substring(0, 80)}... ({v.unita_misura}) {fmt(v.prezzo)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <input placeholder="Capitolo (es. A)" className="px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.capitolo} onChange={e => setRowForm({...rowForm, capitolo: e.target.value})} data-testid="riga-capitolo" />
                  <input placeholder="Titolo capitolo" className="col-span-2 px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.capitolo_titolo} onChange={e => setRowForm({...rowForm, capitolo_titolo: e.target.value})} data-testid="riga-capitolo-titolo" />
                </div>
                <input placeholder="Codice Attivita (Brescia 1/2023 o AZ.XXX)" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" value={rowForm.codice_attivita} onChange={e => setRowForm({...rowForm, codice_attivita: e.target.value})} data-testid="riga-codice" />
                <textarea placeholder="Descrizione lavorazione" rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.descrizione} onChange={e => setRowForm({...rowForm, descrizione: e.target.value})} data-testid="riga-descrizione" />

                <input placeholder="Formula calcolo (es: 7.0 x 0.9 = mq. 6.3)" className="w-full px-3 py-2 border border-blue-200 bg-blue-50 rounded-md text-sm font-mono" value={rowForm.formula_calcolo} onChange={e => setRowForm({...rowForm, formula_calcolo: e.target.value})} data-testid="riga-formula" />

                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Compreso (inclusioni)" className="px-3 py-2 border border-green-200 bg-green-50 rounded-md text-sm" value={rowForm.inclusioni} onChange={e => setRowForm({...rowForm, inclusioni: e.target.value})} data-testid="riga-inclusioni" />
                  <input placeholder="Escluso (es: ponteggio, smaltimento)" className="px-3 py-2 border border-red-200 bg-red-50 rounded-md text-sm" value={rowForm.esclusioni} onChange={e => setRowForm({...rowForm, esclusioni: e.target.value})} data-testid="riga-esclusioni" />
                </div>

                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5 cursor-pointer" data-testid="riga-flag-corpo-label">
                    <input type="checkbox" checked={rowForm.flag_a_corpo} onChange={e => setRowForm({...rowForm, flag_a_corpo: e.target.checked, unita_misura: e.target.checked ? 'a corpo' : rowForm.unita_misura, quantita: e.target.checked ? '1' : rowForm.quantita })} className="accent-primary" data-testid="riga-flag-corpo" />
                    <span>Voce a corpo</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={rowForm.flag_stimata} onChange={e => setRowForm({...rowForm, flag_stimata: e.target.checked})} className="accent-primary" data-testid="riga-flag-stimata" />
                    <span>Quantita STIMATA</span>
                  </label>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <input placeholder="U.M." className="px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.unita_misura} onChange={e => setRowForm({...rowForm, unita_misura: e.target.value})} disabled={rowForm.flag_a_corpo} />
                  <input type="number" step="0.01" placeholder="Quantita" className="px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.quantita} onChange={e => setRowForm({...rowForm, quantita: e.target.value})} disabled={rowForm.flag_a_corpo} />
                  <input type="number" step="0.01" placeholder="Prezzo Rif." className="px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.prezzo_riferimento} onChange={e => setRowForm({...rowForm, prezzo_riferimento: e.target.value})} />
                  <input type="number" step="0.01" placeholder="Prezzo Offerto" className="px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.prezzo_offerto} onChange={e => setRowForm({...rowForm, prezzo_offerto: e.target.value})} />
                </div>
                <textarea placeholder="Note tecniche aggiuntive (opzionale)" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" value={rowForm.note_tecniche} onChange={e => setRowForm({...rowForm, note_tecniche: e.target.value})} data-testid="riga-note" />
              </div>
              </div>
              <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0">
                <button onClick={saveRow} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95" data-testid="save-riga-btn">
                  {editingRow ? 'Salva Modifiche' : 'Aggiungi Riga'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Revisione Modal */}
        {showRevisione && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto p-5 sm:p-7 animate-fade-in shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2"><Bot size={20} className="text-accent-info" /> Revisione AI</h3>
                <button onClick={() => setShowRevisione(false)}><X size={20} className="text-slate-400" /></button>
              </div>
              {revisioneLoading ? (
                <div className="flex items-center gap-3 py-8 justify-center">
                  <div className="w-5 h-5 border-2 border-accent-info border-t-transparent rounded-full animate-spin" />
                  <span className="text-slate-500">L'AI sta analizzando il computo...</span>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap leading-relaxed">{revisione}</div>
              )}
            </div>
          </div>
        )}

        {/* AI Suggerisci Voci Modal */}
        {showSuggerimenti && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto p-5 sm:p-7 animate-fade-in shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2"><Sparkles size={20} className="text-primary" /> AI Cerca Voce nel Prezzario</h3>
                <button onClick={() => setShowSuggerimenti(false)}><X size={20} className="text-slate-400" /></button>
              </div>
              <p className="text-sm text-slate-500 mb-3">Descrivi la lavorazione e l'AI troverà le voci più adatte dal prezzario regionale.</p>
              <div className="flex gap-2 mb-4">
                <input data-testid="ai-sugg-input" placeholder="Es: demolizione pavimento bagno, posa piastrelle..." className="flex-1 px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-primary/50"
                  value={suggDesc} onChange={e => setSuggDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && suggerisciVoci()} />
                <button onClick={suggerisciVoci} disabled={suggLoading || !suggDesc.trim()} className="px-4 py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all disabled:opacity-50 flex items-center gap-1">
                  {suggLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Search size={16} /> Cerca</>}
                </button>
              </div>
              {suggerimenti.length > 0 && (
                <div className="space-y-2">
                  {suggerimenti.map((s, i) => (
                    <button key={s.codice || i} onClick={() => addFromSuggerimento(s)} className="w-full text-left p-3 bg-slate-50 rounded-md hover:bg-primary border border-slate-200 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-primary font-bold">{s.codice}</span>
                        <span className="font-mono text-sm font-bold">{fmt(s.prezzo)}</span>
                      </div>
                      <p className="text-sm mt-1">{s.descrizione}</p>
                      <p className="text-xs text-slate-400 mt-1">{s.unita_misura} {s.motivazione ? `- ${s.motivazione}` : ''}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Matching Modal */}
        {showMatching && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto p-5 sm:p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2"><Link2 size={20} className="text-purple-600" /> Match Automatico Prezzario</h3>
                <button onClick={() => setShowMatching(false)}><X size={20} className="text-slate-400" /></button>
              </div>

              {matchLoading ? (
                <div className="flex flex-col items-center gap-4 py-12">
                  <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
                  <p className="text-slate-500 font-medium">L'AI sta cercando corrispondenze nel prezzario regionale...</p>
                  <p className="text-xs text-slate-400">Analisi di ogni riga con 41.789 voci del prezzario</p>
                </div>
              ) : matchResults.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-slate-500">{matchResults.length} righe da matchare - {Object.values(matchSelected).filter(Boolean).length} selezionate</p>
                    <div className="flex gap-2">
                      <button onClick={() => { const sel = {}; matchResults.forEach(m => { if (m.codice_match !== 'nessun_match') sel[m.riga_id] = true; }); setMatchSelected(sel); }}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50">
                        Seleziona Tutti
                      </button>
                      <button onClick={applySelectedMatches} disabled={!Object.values(matchSelected).filter(Boolean).length} data-testid="apply-matches-btn"
                        className="px-4 py-1.5 bg-purple-600 text-white text-sm font-bold rounded-md hover:bg-purple-700 transition-all disabled:opacity-50 flex items-center gap-1">
                        <CheckCircle size={14} /> Applica Selezionati ({Object.values(matchSelected).filter(Boolean).length})
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {matchResults.map((m, i) => {
                      const hasMatch = m.codice_match && m.codice_match !== 'nessun_match';
                      const confColors = { alta: 'bg-green-100 text-green-700', media: 'bg-yellow-100 text-yellow-800', bassa: 'bg-red-100 text-red-700' };
                      return (
                        <div key={m.riga_id || i} className={`border rounded-md p-3 transition-all ${matchSelected[m.riga_id] ? 'border-purple-400 bg-purple-50/30' : 'border-slate-200'}`} data-testid={`match-row-${i}`}>
                          <div className="flex items-start gap-3">
                            {hasMatch && (
                              <button onClick={() => toggleMatch(m.riga_id)} className="mt-1 flex-shrink-0">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${matchSelected[m.riga_id] ? 'bg-purple-600 border-purple-600' : 'border-slate-300'}`}>
                                  {matchSelected[m.riga_id] && <Check size={12} className="text-white" />}
                                </div>
                              </button>
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Original */}
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded">{m.codice_attuale || '???'}</span>
                                <p className="text-sm font-semibold truncate">{m.descrizione_originale}</p>
                              </div>
                              {/* Match */}
                              {hasMatch ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-slate-400">→</span>
                                  <span className="font-mono text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{m.codice_match}</span>
                                  <p className="text-xs text-slate-600 truncate flex-1">{m.descrizione_match}</p>
                                  <span className="font-mono text-xs font-bold">{fmt(m.prezzo_match)}</span>
                                </div>
                              ) : (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><XCircle size={12} /> Nessuna corrispondenza trovata</p>
                              )}
                              {/* Confidence & Motivation */}
                              <div className="flex items-center gap-2 mt-1.5">
                                {hasMatch && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${confColors[m.confidenza] || 'bg-slate-100'}`}>{m.confidenza}</span>}
                                {m.motivazione && <p className="text-[11px] text-slate-400 italic truncate">{m.motivazione}</p>}
                              </div>
                            </div>
                            {hasMatch && (
                              <button onClick={() => applySingleMatch(m)} className="flex-shrink-0 px-2 py-1 text-xs text-purple-600 font-bold hover:bg-purple-50 rounded transition-colors" title="Applica singolo">
                                <Check size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-center text-slate-400 py-8">Nessuna riga da matchare o tutte le righe hanno già codici ufficiali.</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="animate-fade-in" data-testid="computi-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <FileText size={28} className="text-primary" /> Computi Metrici
          </h1>
          <p className="text-slate-500 mt-1">Carica o crea computi metrici, poi genera preventivi</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="file" ref={fileRef} className="hidden" accept=".pdf,.docx,.doc,.xls,.xlsx,.txt" onChange={uploadFile} />
          <button onClick={() => setShowAIGenerate(true)} data-testid="ai-computo-btn"
            className="px-3 py-2 bg-surface-dark text-white font-bold rounded-md hover:bg-slate-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Sparkles size={16} /> <span className="hidden sm:inline">Genera con</span> AI
          </button>
          <button onClick={() => setShowImportAI(true)} data-testid="import-ai-computo-btn"
            className="px-3 py-2 bg-purple-600 text-white font-bold rounded-md hover:bg-purple-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Wand2 size={16} /> <span className="hidden sm:inline">Importa con</span> AI
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-computo-btn"
            className="px-3 py-2 bg-accent-info text-white font-bold rounded-md hover:bg-blue-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm disabled:opacity-50">
            <Upload size={16} /> {uploading ? 'Caricamento...' : 'Carica'}
          </button>
          <button onClick={createManual} data-testid="new-computo-btn"
            className="px-3 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Plus size={16} /> Nuovo
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {computi.map(c => (
          <div key={c.id} data-testid={`computo-${c.id}`}
            className="w-full bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-all group">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => selectComputo(c)}>
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-bold text-base">{c.titolo || c.codice_computo}</h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${statoColors[c.stato] || 'bg-slate-100'}`}>
                    {statoLabels[c.stato] || c.stato}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${c.origine === 'importato' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-600'}`}>
                    {c.origine === 'importato' ? 'Importato' : 'Interno'}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{c.codice_computo} - v{c.versione} - {c.righe_totali || '?'} righe</p>
              </div>
              <ChevronRight size={20} className="text-slate-300 group-hover:text-primary transition-colors" />
            </div>
            <div className="flex justify-end mt-2">
              <RowActions entity="computi" id={c.id} label="il computo metrico" onChange={load} compact />
            </div>
          </div>
        ))}
        {computi.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <FileText size={48} className="mx-auto mb-3 text-slate-300" />
            <p className="font-semibold">Nessun computo metrico</p>
            <p className="text-sm mt-1">Carica un computo da file (PDF, DOCX, XLS) o creane uno manualmente</p>
          </div>
        )}
      </div>

      {/* AI Generate Computo Modal */}
      {showAIGenerate && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto animate-scale-in shadow-2xl">
            <div className="bg-gradient-to-r from-primary to-amber-50 px-6 py-5 border-b border-slate-200 flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-lg bg-primary text-white flex items-center justify-center flex-shrink-0">
                  <Sparkles size={22} />
                </div>
                <div>
                  <h3 className="text-2xl font-extrabold text-slate-900">Genera Computo Metrico con AI</h3>
                  <p className="text-sm text-slate-600 mt-0.5">L'AI genera capitoli, voci dal prezzario, quantita, prezzi e formule esplicite</p>
                </div>
              </div>
              <button onClick={() => setShowAIGenerate(false)} className="p-2 hover:bg-white/50 rounded-md transition-colors"><X size={22} className="text-slate-500" /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {cataloghi.length > 1 && (
                <div data-testid="ai-computo-catalogo-switcher">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">Prezzario di riferimento</label>
                  <div className="flex flex-wrap gap-2">
                    {cataloghi.map(c => (
                      <button key={c.id} type="button" onClick={() => setSelectedCatalogo(c.id)}
                        className={`text-sm px-4 py-2 rounded-md font-bold transition-all ${selectedCatalogo === c.id
                          ? (c.id === 'cer_2025' ? 'bg-indigo-600 text-white shadow-md' : c.id === 'aziendale' ? 'bg-green-600 text-white shadow-md' : 'bg-blue-600 text-white shadow-md')
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                        {c.id === 'cer_2025' ? 'CER 2025' : c.id === 'lombardia_2025' ? 'Lombardia 2025' : c.nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">Descrizione lavorazione</label>
                <textarea data-testid="ai-computo-desc"
                  placeholder="Es: Ristrutturazione completa bagno 8mq con demolizione pavimento e rivestimenti esistenti, nuovo impianto idrico, posa piastrelle pavimento e rivestimento, sanitari, tinteggiatura..."
                  rows={8}
                  className="w-full px-4 py-3 border border-slate-300 rounded-md text-base leading-relaxed focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  value={aiDesc} onChange={e => setAiDesc(e.target.value)} />
                <p className="text-xs text-slate-500 mt-1.5">Suggerimento: indica metratura, materiali e lavorazioni specifiche per un computo piu accurato</p>
              </div>

              <button data-testid="ai-computo-submit" onClick={generateAIComputo} disabled={aiLoading || !aiDesc.trim()}
                className="w-full py-3.5 bg-primary text-white font-bold text-base rounded-md hover:bg-primary-hover transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md">
                {aiLoading ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> {aiProgress || 'Generazione in corso...'}</>
                ) : (
                  <><Sparkles size={20} /> Genera Computo Metrico</>
                )}
              </button>
              {aiLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-center" data-testid="ai-progress-text">
                  <p className="text-sm text-blue-900 font-medium">L'AI sta lavorando...</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Job: <code className="font-mono">{aiJobId?.substring(0, 8)}...</code> - Puo richiedere fino a 2 minuti. Il job continua anche se chiudi la finestra.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showImportAI && (
        <ImportAIModal tipo="computo" onClose={() => setShowImportAI(false)} onImport={handleImportAI} />
      )}
    </div>
  );
}
